import { type AxiosRequestConfig, type AxiosResponse } from 'axios';
import http from '@/core/api/http';
import {
  RequestStatus,
  type ApiResponse,
  type BaseResponse,
  type DownloadResponse,
  type ErrorResponse,
  type MappedResponse,
  type PaginatedRequest
} from '@/core/api/types';
import { RequestHeaders } from '@/constants/requestHeaders';

export async function useDownload<R, P extends object = never>(
  url: string,
  config: AxiosRequestConfig = {
    responseType: 'blob',
    headers: {
      [RequestHeaders.UseReportingUrl]: 'true'
    }
  },
  payload?: P
): Promise<DownloadResponse<R>> {
  const params = generateParams(payload || {});

  return mapResponse<R, DownloadResponse<R>>(http().get(url, { params, ...config }));
}

export async function useGet<R, P extends object = never>(
  url: string,
  payload?: P,
  config?: AxiosRequestConfig
): Promise<ApiResponse<R>> {
  const params = generateParams(payload || {});
  return mapResponse<R>(http().get(url, { params, ...config }));
}

export async function useGetPaged<R, P extends PaginatedRequest>(
  url: string,
  payload: P
): Promise<ApiResponse<R>> {
  return useGet<R, P>(url, payload);
}

export async function usePost<R, B>(
  url: string,
  payload: B,
  options?: AxiosRequestConfig
): Promise<ApiResponse<R>> {
  return mapResponse<R>(http().post(url, payload, options));
}

export async function usePut<R, B>(
  url: string,
  payload: B,
  options?: AxiosRequestConfig
): Promise<ApiResponse<R>> {
  return mapResponse<R>(http().put(url, payload, options));
}

export async function usePatch<R, B>(
  url: string,
  payload: B,
  options?: AxiosRequestConfig
): Promise<ApiResponse<R>> {
  return mapResponse<R>(http().patch(url, payload, options));
}

export async function useDelete<R>(
  url: string,
  config?: AxiosRequestConfig
): Promise<ApiResponse<R>> {
  return mapResponse<R>(http().delete(url, config));
}

export function generateParams<T extends object>(payload: T): URLSearchParams {
  const params = new URLSearchParams();
  for (const key in payload) {
    if (payload[key] !== undefined) {
      params.append(key, payload[key] as string);
    }
  }
  return params;
}

export async function mapResponse<T, R = ApiResponse<T>>(
  callback: Promise<AxiosResponse<BaseResponse<T>>>
): Promise<R> {
  try {
    const response = await callback;
    return {
      status: RequestStatus.SUCCESS,
      data: response.data.payload ?? response.data,
      error: null,
      networkResponse: response
    } as R;
  } catch (error) {
    return {
      status: RequestStatus.ERROR,
      data: null,
      error: error as ErrorResponse<T>,
      networkResponse: null
    } as R;
  }
}

export function unpackMappedResponse<T>(response: ApiResponse<T>): MappedResponse<T> {
  return {
    status: response.status,
    data: response.data
  } as MappedResponse<T>;
}

export function unpackMappedResponseWithKey<T>(
  response: ApiResponse<T>,
  key: keyof T
): MappedResponse<T[keyof T]> {
  if (response.status === RequestStatus.ERROR) {
    return {
      status: response.status,
      data: null
    };
  }
  return {
    status: response.status,
    data: response.data[key]
  };
}
